import { Injectable } from '@angular/core';
import { WebServiceEndpoints } from '../../web-service-endpoints';
import { HttpClient } from '@angular/common/http';
import { ISpecialistActivities } from './data/models/specialist-activities.model';
import { Specialist } from '@common/services/specialist/data/models/specialist.model';
import { SetSpecialistDto } from '@common/services/specialist/data/dto/set-specialist.dto';
import { IGetSpecialistActivitiesResponse } from '@common/services/specialist/data/responses/get-specialist-activities.response';
import { SpecialistActivityTypeEnum } from '@common/services/specialist/data/enum/specialist-activity-type.enum';
import { Store } from '@ngrx/store';

const SPECIALIST_KEY = 'specialist';

@Injectable({
  providedIn: 'root',
})
export class SpecialistsService {
  private _specialist: Specialist;
  constructor(private readonly http: HttpClient, private store: Store) {}

  public get specialist(): Specialist {
    let setSpecialistDto: SetSpecialistDto;
    const specialist = this._specialist;
    if (!specialist || !specialist?.id || !specialist?.isSkillsInitialized) {
      setSpecialistDto = this.tryGetSpecialistDtoFromStorage();
      if (setSpecialistDto) {
        this.setSpecialist(setSpecialistDto);
      }
    }

    return this._specialist;
  }

  public setAndSaveSpecialist(setSpecialistDto: SetSpecialistDto): void {
    this.setSpecialist(setSpecialistDto);
    this.saveSpecialistToStorage(this._specialist);
  }

  public removeSpecialist(): void {
    this._specialist = null;
  }

  public async setSkills(skillIds: string[]): Promise<void> {
    const body = {
      SkillIds: skillIds,
      Name: this.specialist.username,
      FirstName: this.specialist.firstName,
      LastName: this.specialist.lastName,
    };
    await this.http.post<{ SpecialistId: string }>(WebServiceEndpoints.Specialist.setSkills, body).toPromise();
  }

  public async createRoomAccessToken(VideoRoomId: string): Promise<string> {
    const body = { VideoRoomId };
    const result = await this.http
      .post<{ AccessToken: string }>(WebServiceEndpoints.Specialist.accessToken, body)
      .toPromise();

    return result.AccessToken;
  }

  public async createCapabilityToken(): Promise<string> {
    const result = await this.http
      .post<{ CapabilityToken: string }>(WebServiceEndpoints.Specialist.capabilityToken, {
        Identity: this.specialist.id,
      })
      .toPromise();

    return result.CapabilityToken;
  }

  public async createToken(): Promise<string> {
    const result = await this.http
      .post<{ Token: string }>(WebServiceEndpoints.Specialist.token, {
        Identity: this.specialist.id,
      })
      .toPromise();

    return result.Token;
  }

  public async getSpecialistActivities(): Promise<ISpecialistActivities> {
    try {
      const response = await this.http
        .post<IGetSpecialistActivitiesResponse>(WebServiceEndpoints.Specialist.getActivities, {})
        .toPromise();
      return response.Activities.reduce((acc, value) => {
        switch (value.Type) {
          case SpecialistActivityTypeEnum.Available:
            acc.availableActivityId = value.Id;
            break;
          case SpecialistActivityTypeEnum.Unavailable:
            acc.unavailableActivityId = value.Id;
            break;
          case SpecialistActivityTypeEnum.Offline:
            acc.offlineActivityId = value.Id;
            break;
          case SpecialistActivityTypeEnum.OnLiveChatCall:
            acc.onLiveChatCallActivityId = value.Id;
            break;
          case SpecialistActivityTypeEnum.OnSmsInviteCall:
            acc.onSmsInviteCallActivityId = value.Id;
            break;
          default:
        }
        return acc;
      }, {} as ISpecialistActivities);
    } finally {
      // an exception is designed to caught in enclosing method
    }
  }

  public async getSkills(): Promise<string[]> {
    const result = await this.http
      .post<{ SkillIds: string[] }>(WebServiceEndpoints.Specialist.skills, {
        SpecialistId: this.specialist.id,
      })
      .toPromise();

    return result.SkillIds;
  }

  public setSpecialistIsEnrolledInDocusign(userId: string): void {
    const setSpecialistDto = this.tryGetSpecialistDtoFromStorage();
    if (setSpecialistDto) {
      setSpecialistDto.IsEnrolledInDocuSign = !!userId;
      setSpecialistDto.DocusignUserId = userId ?? null;
    }
    this.setAndSaveSpecialist(setSpecialistDto);
  }

  private setSpecialist(setSpecialistDto: SetSpecialistDto): void {
    this._specialist = new Specialist(
      setSpecialistDto.Id,
      setSpecialistDto.UserName,
      setSpecialistDto.FirstName,
      setSpecialistDto.LastName,
      setSpecialistDto.IsAdmin,
      setSpecialistDto.IsEnrolledInDocuSign,
      setSpecialistDto.DocusignUserId,
      setSpecialistDto.IsSkillsInitialized,
    );
  }

  private saveSpecialistToStorage(specialist: Specialist): void {
    sessionStorage.setItem(SPECIALIST_KEY, JSON.stringify(specialist));
  }

  private tryGetSpecialistDtoFromStorage(): SetSpecialistDto {
    let setSpecialistDto: SetSpecialistDto;
    try {
      const savedSpecialistString = sessionStorage.getItem(SPECIALIST_KEY);
      if (savedSpecialistString) {
        const parsedSavedSpecialist = JSON.parse(savedSpecialistString);
        setSpecialistDto = {
          Id: parsedSavedSpecialist.id,
          UserName: parsedSavedSpecialist.username,
          FirstName: parsedSavedSpecialist.firstName,
          LastName: parsedSavedSpecialist.lastName,
          IsAdmin: parsedSavedSpecialist.isAdmin,
          IsEnrolledInDocuSign: parsedSavedSpecialist.isEnrolledInDocuSign,
          DocusignUserId: parsedSavedSpecialist.docusignUserId,
          IsSkillsInitialized: parsedSavedSpecialist.isSkillsInitialized,
        };
      }
    } catch {
      return null;
    }
    return setSpecialistDto;
  }
}
